import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import PanelLayout from '../../components/PanelLayout';
import {
  StatCard, Card, CardBody, CardHeader, Button, StatusBadge, Table,
  Modal, FormGroup, Label, Input, Select, Textarea, EmptyState,
  PageLoader, Tabs, SectionHeader, Badge
} from '../../components/UI';
import { useAuth } from '../../context/AuthContext';
import api, { fmt } from '../../utils/api';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const NAV = [
  { to:'/vendor', icon:'📊', label:'Dashboard' },
  { to:'/vendor/orders', icon:'🛒', label:'All Orders' },
  { to:'/vendor/reports', icon:'📈', label:'Sales Reports' },
];

function RequireVendor({ children }) {
  const { role } = useAuth();
  if (role !== 'vendor' && role !== 'admin') return <Navigate to="/login" replace />;
  return children;
}

/* ── Tracking Timeline ──────────────────────────────────────── */
function TrackingTimeline({ tracking }) {
  const STEPS = ['pending','confirmed','packed','shipped','out_for_delivery','delivered'];
  const currentIdx = tracking?.length ? STEPS.indexOf(tracking[tracking.length-1]?.status) : 0;

  return (
    <div style={{ position:'relative', paddingLeft:28, borderLeft:'2px solid var(--gray-200)' }}>
      {(tracking||[]).map((t,i) => (
        <div key={t.id} style={{ position:'relative', paddingBottom:16 }}>
          <div style={{ position:'absolute', left:-37, top:2, width:16, height:16, borderRadius:'50%', background:'var(--primary)', border:'2px solid var(--primary)' }} />
          <div style={{ fontWeight:600, fontSize:'0.88rem', color:'var(--gray-800)' }}>{fmt.orderStatus(t.status)}</div>
          {t.courier_name && (
            <div style={{ fontSize:'0.82rem', color:'var(--gray-600)' }}>
              {t.courier_name}{t.tracking_number ? ` — Tracking: ${t.tracking_number}` : ''}
            </div>
          )}
          {t.notes && <div style={{ fontSize:'0.8rem', color:'var(--gray-500)' }}>{t.notes}</div>}
          <div style={{ fontSize:'0.75rem', color:'var(--gray-400)', marginTop:2 }}>{fmt.datetime(t.created_at)}</div>
        </div>
      ))}
    </div>
  );
}

/* ── Dashboard ──────────────────────────────────────────────── */
function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/vendor/dashboard').then(setData).finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader />;

  return (
    <div>
      <SectionHeader title="Vendor Dashboard" subtitle="Your orders & performance at a glance" />

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:16, marginBottom:28 }}>
        <StatCard icon="🛒" label="Total Orders" value={data?.stats?.total_orders || 0} color="blue" />
        <StatCard icon="⏳" label="Pending" value={data?.stats?.pending || 0} color="orange" />
        <StatCard icon="🚚" label="Shipped" value={data?.stats?.shipped || 0} color="purple" />
        <StatCard icon="✅" label="Delivered" value={data?.stats?.delivered || 0} color="green" />
        <StatCard icon="💰" label="Revenue" value={fmt.currency(data?.stats?.revenue || 0)} color="green" />
      </div>

      <Card>
        <CardHeader><h4>Recent Orders</h4></CardHeader>
        <Table
          columns={[
            { key:'order_number', label:'Order', render:r=><span style={{fontWeight:700}}>#{r.order_number}</span> },
            { key:'customer_name', label:'Customer' },
            { key:'created_at', label:'Date', render:r=>fmt.date(r.created_at) },
            { key:'total', label:'Total', render:r=><span style={{fontWeight:700,color:'var(--primary)'}}>{fmt.currency(r.total)}</span> },
            { key:'order_status', label:'Status', render:r=><StatusBadge status={r.order_status} /> },
          ]}
          data={data?.recentOrders || []}
          emptyMsg="No recent orders"
        />
      </Card>
    </div>
  );
}

/* ── Orders ─────────────────────────────────────────────────── */
function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [detailOrder, setDetailOrder] = useState(null);
  const [updateModal, setUpdateModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ order_status:'packed', courier_name:'', tracking_number:'', notes:'' });

  const load = useCallback(() => {
    setLoading(true);
    const q = tab !== 'all' ? `?status=${tab}` : '';
    api.get(`/vendor/orders${q}`).then(d => setOrders(d.orders||[])).finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async (order) => {
    try {
      const d = await api.get(`/vendor/orders/${order.id}`);
      setDetailOrder(d.order);
    } catch { toast.error('Could not load order details'); }
  };

  const openUpdateModal = (order) => {
    setDetailOrder(order);
    setStatusForm({ order_status: order.order_status === 'confirmed' ? 'packed' : order.order_status, courier_name:'', tracking_number:'', notes:'' });
    setUpdateModal(true);
  };

  const updateStatus = async () => {
    try {
      await api.patch(`/vendor/orders/${detailOrder.id}/status`, statusForm);
      toast.success('Order status updated!');
      setUpdateModal(false);
      setDetailOrder(null);
      load();
    } catch(e) { toast.error(e.message); }
  };

  const TABS = [
    { value:'all', label:'All' },
    { value:'pending', label:'Pending' },
    { value:'confirmed', label:'Confirmed' },
    { value:'packed', label:'Packed' },
    { value:'shipped', label:'Shipped' },
    { value:'out_for_delivery', label:'Out for Delivery' },
    { value:'delivered', label:'Delivered' },
  ];

  const NEXT_STATUS = {
    pending: 'confirmed', confirmed: 'packed', packed: 'shipped',
    shipped: 'out_for_delivery', out_for_delivery: 'delivered'
  };

  return (
    <div>
      <SectionHeader title="All Orders" subtitle={`${orders.length} orders`} />
      <Tabs tabs={TABS} active={tab} onChange={t => setTab(t)} />

      <Table loading={loading}
        columns={[
          { key:'order_number', label:'Order', render:r=>(
            <button onClick={() => loadDetail(r)} style={{ background:'none', border:'none', cursor:'pointer', fontWeight:700, color:'var(--primary)', textDecoration:'underline' }}>
              #{r.order_number}
            </button>
          )},
          { key:'customer', label:'Customer', render:r=>(
            <div>
              <div style={{fontWeight:500}}>{r.customer_name}</div>
              <div style={{fontSize:'0.78rem',color:'var(--gray-400)'}}>{r.customer_email}</div>
              {r.customer_phone && <div style={{fontSize:'0.78rem',color:'var(--gray-400)'}}>{r.customer_phone}</div>}
            </div>
          )},
          { key:'created_at', label:'Date', render:r=>fmt.date(r.created_at) },
          { key:'total', label:'Amount', render:r=><span style={{fontWeight:700,color:'var(--primary)'}}>{fmt.currency(r.total)}</span> },
          { key:'payment_status', label:'Payment', render:r=><StatusBadge status={r.payment_status} /> },
          { key:'order_status', label:'Status', render:r=><StatusBadge status={r.order_status} /> },
          { key:'actions', label:'Action', render:r=>
            NEXT_STATUS[r.order_status] ? (
              <Button $size="sm" onClick={() => openUpdateModal(r)}>
                Mark as {fmt.orderStatus(NEXT_STATUS[r.order_status])}
              </Button>
            ) : (
              <span style={{fontSize:'0.82rem',color:'var(--gray-400)'}}>
                {r.order_status === 'delivered' ? '✅ Done' : '—'}
              </span>
            )
          },
        ]}
        data={orders}
        emptyMsg="No orders in this category"
      />

      {/* Order Detail Modal */}
      <Modal open={!!detailOrder && !updateModal} onClose={() => setDetailOrder(null)}
        title={`Order #${detailOrder?.order_number}`} width="680px">
        {detailOrder && (
          <div>
            {/* Customer info */}
            <div style={{ background:'var(--gray-50)', borderRadius:'var(--radius)', padding:14, marginBottom:16 }}>
              <h4 style={{ marginBottom:8, fontSize:'0.9rem' }}>Customer Details</h4>
              <div style={{ fontSize:'0.88rem', display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                <div><strong>Name:</strong> {detailOrder.customer_name}</div>
                <div><strong>Email:</strong> {detailOrder.customer_email}</div>
                <div><strong>Phone:</strong> {detailOrder.customer_phone||'—'}</div>
                <div><strong>Payment:</strong> <StatusBadge status={detailOrder.payment_status} /></div>
              </div>
              {detailOrder.shipping_address && (
                <div style={{ marginTop:8, fontSize:'0.88rem' }}>
                  <strong>Delivery Address:</strong>{' '}
                  {(() => {
                    try {
                      const a = typeof detailOrder.shipping_address === 'string' ? JSON.parse(detailOrder.shipping_address) : detailOrder.shipping_address;
                      return `${a.address_line1}, ${a.city}, ${a.state} ${a.zip}, ${a.country}`;
                    } catch { return '—'; }
                  })()}
                </div>
              )}
            </div>

            {/* Items */}
            <h4 style={{ marginBottom:10 }}>Order Items</h4>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16 }}>
              {(detailOrder.items||[]).map(item => (
                <div key={item.id} style={{ display:'flex', gap:12, padding:10, background:'var(--gray-50)', borderRadius:'var(--radius)', fontSize:'0.88rem' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600 }}>{item.product_name}</div>
                    {item.color && <div style={{ color:'var(--gray-500)' }}>Color: {item.color}{item.size ? ` | Size: ${item.size}` : ''}</div>}
                  </div>
                  <div>Qty: <strong>{item.quantity}</strong></div>
                  <div>Price: <strong>{fmt.currency(item.price)}</strong></div>
                  <div style={{ fontWeight:700, color:'var(--primary)' }}>{fmt.currency(item.total)}</div>
                </div>
              ))}
            </div>

            {/* Total */}
            <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:20 }}>
              <div style={{ background:'var(--primary-50)', padding:'10px 20px', borderRadius:'var(--radius)', fontSize:'1rem', fontWeight:700 }}>
                Total: {fmt.currency(detailOrder.total)}
              </div>
            </div>

            {/* Tracking */}
            <h4 style={{ marginBottom:10 }}>Tracking History</h4>
            <TrackingTimeline tracking={detailOrder.tracking} />

            {/* Update button */}
            {NEXT_STATUS[detailOrder.order_status] && (
              <div style={{ marginTop:20 }}>
                <Button $block onClick={() => { setUpdateModal(true); setStatusForm({ order_status: NEXT_STATUS[detailOrder.order_status], courier_name:'', tracking_number:'', notes:'' }); }}>
                  Update Status → {fmt.orderStatus(NEXT_STATUS[detailOrder.order_status])}
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Update Status Modal */}
      <Modal open={updateModal} onClose={() => { setUpdateModal(false); }}
        title="Update Order Status"
        footer={<><Button $variant="secondary" onClick={() => setUpdateModal(false)}>Cancel</Button><Button onClick={updateStatus}>Save Update</Button></>}>
        <FormGroup>
          <Label>New Status</Label>
          <Select value={statusForm.order_status} onChange={e => setStatusForm(f => ({ ...f, order_status: e.target.value }))}>
            {['confirmed','packed','shipped','out_for_delivery','delivered'].map(s => (
              <option key={s} value={s}>{fmt.orderStatus(s)}</option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>Courier Name</Label>
          <Input value={statusForm.courier_name} onChange={e => setStatusForm(f => ({ ...f, courier_name: e.target.value }))} placeholder="e.g. FedEx, Blue Dart, India Post" />
        </FormGroup>
        <FormGroup>
          <Label>Tracking Number</Label>
          <Input value={statusForm.tracking_number} onChange={e => setStatusForm(f => ({ ...f, tracking_number: e.target.value }))} placeholder="Enter tracking number" />
        </FormGroup>
        <FormGroup>
          <Label>Notes (optional)</Label>
          <Textarea value={statusForm.notes} onChange={e => setStatusForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any extra delivery notes..." style={{ minHeight:80 }} />
        </FormGroup>
      </Modal>
    </div>
  );
}

/* ── Reports ─────────────────────────────────────────────────── */
function Reports() {
  const [period, setPeriod] = useState('30');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/vendor/reports?period=${period}`).then(setData).finally(() => setLoading(false));
  }, [period]);

  return (
    <div>
      <SectionHeader title="Sales Reports"
        action={
          <Select value={period} onChange={e => setPeriod(e.target.value)} style={{ width:'auto' }}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </Select>
        }
      />
      {loading ? <PageLoader /> : (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:16, marginBottom:24 }}>
            <StatCard icon="💰" label="Total Revenue" value={fmt.currency((data?.daily||[]).reduce((s,d)=>s+(parseFloat(d.revenue)||0),0))} color="green" />
            <StatCard icon="🛒" label="Total Orders" value={(data?.daily||[]).reduce((s,d)=>s+(parseInt(d.orders)||0),0)} color="blue" />
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
            <Card>
              <CardHeader><h4>Revenue ({period} days)</h4></CardHeader>
              <CardBody>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data?.daily||[]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize:11 }} tickFormatter={d => d?.slice(5)} />
                    <YAxis tick={{ fontSize:11 }} />
                    <Tooltip formatter={v => fmt.currency(v)} />
                    <Bar dataKey="revenue" fill="var(--primary)" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardBody>
            </Card>

            <Card>
              <CardHeader><h4>Top Products</h4></CardHeader>
              <Table
                columns={[
                  { key:'name', label:'Product', render:r=><span style={{fontWeight:500,fontSize:'0.88rem'}}>{r.name}</span> },
                  { key:'sold', label:'Units Sold', render:r=><Badge $color="primary">{r.sold}</Badge> },
                ]}
                data={data?.topProducts||[]}
                emptyMsg="No sales data"
              />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Vendor Router ───────────────────────────────────────────── */
export default function VendorPanel() {
  return (
    <RequireVendor>
      <PanelLayout navItems={NAV} role="Vendor">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="orders" element={<Orders />} />
          <Route path="reports" element={<Reports />} />
          <Route path="*" element={<Navigate to="/vendor" replace />} />
        </Routes>
      </PanelLayout>
    </RequireVendor>
  );
}
